import _slicedToArray from "/workspace/node_modules/@babel/runtime/helpers/esm/slicedToArray";

/**
 * @license
 * Copyright 2020 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import { backend_util, RotateWithOffset, util } from '@tensorflow/tfjs-core';
export var rotateWithOffsetConfig = {
  kernelName: RotateWithOffset,
  backendName: 'cpu',
  kernelFunc: function kernelFunc(_ref) {
    var inputs = _ref.inputs,
        attrs = _ref.attrs,
        backend = _ref.backend;
    var image = inputs.image;
    var radians = attrs.radians,
        fillValue = attrs.fillValue,
        center = attrs.center;
    var cpuBackend = backend;
    var output = util.getTypedArrayFromDType(image.dtype, util.sizeFromShape(image.shape));

    var _image$shape = _slicedToArray(image.shape, 4),
        batch = _image$shape[0],
        imageHeight = _image$shape[1],
        imageWidth = _image$shape[2],
        numChannels = _image$shape[3];

    var _backend_util$getImag = backend_util.getImageCenter(center, imageHeight, imageWidth),
        _backend_util$getImag2 = _slicedToArray(_backend_util$getImag, 2),
        centerX = _backend_util$getImag2[0],
        centerY = _backend_util$getImag2[1];

    var fullOpacityValue = 255;
    var sinFactor = Math.sin(radians);
    var cosFactor = Math.cos(radians);
    var imageVals = cpuBackend.data.get(image.dataId).values;

    for (var batchIdx = 0; batchIdx < batch; batchIdx++) {
      var batchOffset = batchIdx * imageWidth * imageHeight * numChannels;

      for (var row = 0; row < imageHeight; row++) {
        var rowOffset = row * (imageWidth * numChannels);

        for (var col = 0; col < imageWidth; col++) {
          var colOffset = col * numChannels;

          for (var channel = 0; channel < numChannels; channel++) {
            var coords = [batch, row, col, channel];
            var x = coords[2];
            var y = coords[1]; // coordX/coordY are the result of rotating and translating x/y.

            var coordX = (x - centerX) * cosFactor - (y - centerY) * sinFactor;
            var coordY = (x - centerX) * sinFactor + (y - centerY) * cosFactor;
            coordX = Math.round(coordX + centerX);
            coordY = Math.round(coordY + centerY);
            var outputValue = fillValue;

            if (typeof fillValue !== 'number') {
              if (channel === 3) {
                outputValue = fullOpacityValue;
              } else {
                outputValue = fillValue[channel];
              }
            } // If the coordinate position falls within the image boundaries...


            if (coordX >= 0 && coordX < imageWidth && coordY >= 0 && coordY < imageHeight) {
              // set the output to the image value at the coordinate position.
              var rotatedRowOffset = coordY * (imageWidth * numChannels);
              var rotatedColOffset = coordX * numChannels;
              var imageIdx = batchOffset + rotatedRowOffset + rotatedColOffset + channel;
              outputValue = imageVals[imageIdx];
            }

            var outIdx = batchOffset + rowOffset + colOffset + channel;
            output[outIdx] = outputValue;
          }
        }
      }
    }

    var dataId = cpuBackend.write(output, image.shape, image.dtype);
    return {
      dataId: dataId,
      shape: image.shape,
      dtype: image.dtype
    };
  }
};